/**
 * URL Discount Handler
 * Automatically applies discount codes from URL on page load
 * Handles both /discount/CODE12345 and ?discount=CODE12345 formats
 */

import { fetchConfig } from '@theme/utilities';
import {
  handleUrlDiscount,
  storeDiscountCode,
  getStoredDiscountCode,
} from './discount-code-handler.js';

/**
 * Opens the cart drawer if VIP2025 discount code is present
 */
function openCartDrawerForVIP2025() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  } else {
    customElements.whenDefined('cart-drawer-component').then(() => {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    });
  }
}

/**
 * Applies discount code from URL if present
 * Only applies if cart discount component exists on page
 */
async function applyUrlDiscount() {
  const discountCode = handleUrlDiscount();
  
  if (!discountCode) return;

  const cartDiscountComponent = document.querySelector('cart-discount-component');
  if (!cartDiscountComponent) {
    storeDiscountCode(discountCode);
    if (discountCode.toUpperCase() === 'VIP2025') {
      openCartDrawerForVIP2025();
    }
    return;
  }

  const sectionId = cartDiscountComponent instanceof HTMLElement ? cartDiscountComponent.dataset.sectionId : null;
  if (!sectionId) return;

  const form = cartDiscountComponent.querySelector('form');
  if (!form) return;

  const discountInput = form.querySelector('input[name="discount"]');
  if (!(discountInput instanceof HTMLInputElement)) return;

  const existingDiscounts = Array.from(
    cartDiscountComponent.querySelectorAll('.cart-discount__pill')
  )
    .map((pill) => {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        return pill.dataset.discountCode;
      }
      return null;
    })
    .filter(Boolean);

  if (existingDiscounts.includes(discountCode)) {
    storeDiscountCode(discountCode);
    if (discountCode.toUpperCase() === 'VIP2025') {
      openCartDrawerForVIP2025();
    }
    return;
  }

  try {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: [...existingDiscounts, discountCode].join(','),
        sections: [sectionId],
        attributes: {
          _discount_code: discountCode.trim(),
        },
      }),
    });

    const response = await fetch((typeof Theme !== 'undefined' && Theme?.routes?.cart_update_url) || '/cart/update.js', config);

    const data = await response.json();

    if (
      data.discount_codes?.find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCode && discount.applicable === false;
        }
      )
    ) {
      return;
    }

    storeDiscountCode(discountCode);

    if (data.sections && data.sections[sectionId]) {
      const { morphSection } = await import('@theme/section-renderer');
      morphSection(sectionId, data.sections[sectionId]);
    }

    if (discountCode.toUpperCase() === 'VIP2025') {
      openCartDrawerForVIP2025();
    }
  } catch (error) {
    console.warn('Failed to apply URL discount:', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyUrlDiscount);
} else {
  applyUrlDiscount();
}

